import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import { triggerTextDownload, triggerCsvDownload, dateStamp } from '../../services/downloadHelpers';
import ReportConfigurationPanel from './components/ReportConfigurationPanel';
import ReportsLibrary from './components/ReportsLibrary';
import AuditTrailPanel from './components/AuditTrailPanel';
import AIActCompliancePanel from './components/AIActCompliancePanel';
import ReportPreviewModal from './components/ReportPreviewModal';
import SkillOntologyPanel from './components/SkillOntologyPanel';
import JobDescriptionLibrary from './components/JobDescriptionLibrary';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useTenant } from '../../context/TenantContext';
import { useTranslation } from 'react-i18next';
import { buildComplianceReports } from '../../data/derivedReports';

const ComplianceReports = () => {
  const { tenant, isTransitioning } = useTenant();
  const { t } = useTranslation();
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [activeView, setActiveView] = useState('reports');
  const [jobDescriptions, setJobDescriptions] = useState([]);

  // Mock data for reports
  const [reports, setReports] = useState([]);

  // Rebuild reports list when active tenant changes
  useEffect(() => {
    setReports(buildComplianceReports(tenant));
  }, [tenant.id]);

  // Mock audit trail data
  const auditLogs = [
    {
      id: 'audit-001',
      action: 'report_generated',
      description: 'Q4 2024 EU Pay Transparency Report generated successfully',
      userName: 'Sarah Johnson',
      userRole: 'admin',
      timestamp: '2024-10-08T10:30:15Z',
      ipAddress: '192.168.1.100',
      userAgent: 'Chrome/118.0.0.0',
      reportId: 'rpt-001',
      details: 'Report generated with 2,847 employee records, 98% data completeness'
    },
    {
      id: 'audit-002',
      action: 'report_exported',
      description: 'Germany compliance report exported as PDF',
      userName: 'Michael Weber',
      userRole: 'manager',
      timestamp: '2024-10-08T09:45:22Z',
      ipAddress: '192.168.1.105',
      userAgent: 'Firefox/119.0',
      reportId: 'rpt-002',
      details: 'PDF export completed, file size: 1.8MB'
    },
    {
      id: 'audit-003',
      action: 'access_denied',
      description: 'Unauthorized access attempt to compliance reports',
      userName: 'Unknown User',
      userRole: 'unknown',
      timestamp: '2024-10-08T08:22:11Z',
      ipAddress: '203.0.113.45',
      userAgent: 'curl/7.68.0',
      reportId: null,
      details: 'Access denied - insufficient permissions for reports module'
    },
    {
      id: 'audit-004',
      action: 'config_changed',
      description: 'Report configuration template updated',
      userName: 'Admin User',
      userRole: 'admin',
      timestamp: '2024-10-07T17:30:45Z',
      ipAddress: '192.168.1.101',
      userAgent: 'Chrome/118.0.0.0',
      reportId: null,
      details: 'Updated EU directive template to include new Article 9 requirements'
    },
    {
      id: 'audit-005',
      action: 'report_previewed',
      description: 'France Index Égalité report previewed',
      userName: 'Marie Dubois',
      userRole: 'manager',
      timestamp: '2024-10-07T16:50:33Z',
      ipAddress: '192.168.1.110',
      userAgent: 'Safari/17.0',
      reportId: 'rpt-003',
      details: 'Report preview accessed during generation process'
    }
  ];

  const handleGenerateReport = async (config) => {
    setIsGenerating(true);
    
    // Simulate report generation
    setTimeout(() => {
      const newReport = {
        id: `rpt-${String(reports?.length + 1)?.padStart(3, '0')}`,
        name: `${config?.template?.replace(/-/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())} Report`,
        template: config?.template,
        period: config?.period,
        scope: 'Global Organization',
        status: 'generated',
        createdAt: new Date()?.toISOString(),
        fileSize: Math.floor(Math.random() * 3000000) + 1000000,
        checksum: `sha256:${Math.random()?.toString(36)?.substring(2, 15)}...`,
        isImmutable: true,
        availableFormats: ['pdf', 'csv', 'xlsx'],
        createdBy: 'Admin User'
      };
      
      setReports(prev => [newReport, ...prev]);
      setIsGenerating(false);
    }, 3000);
  };

  const handleUpdateJobDescriptions = (generatedData) => {
    setJobDescriptions(prev => [...generatedData?.jobDescriptions, ...prev]);
  };

  const handleExportReport = (report) => {
    const lines = [
      '='.repeat(72),
      'CLEAREQUAL, COMPLIANCE REPORT EXPORT',
      'EU Pay Transparency Directive 2023/970',
      '='.repeat(72),
      '',
      `Report ID:        ${report?.id || ''}`,
      `Report name:      ${report?.name || ''}`,
      `Report type:      ${report?.type || ''}`,
      `Reporting period: ${report?.period || ''}`,
      `Status:           ${report?.status || ''}`,
      `Created:          ${report?.createdAt || ''}`,
      '',
      `Export generated: ${dateStamp()}`,
    ].join('\n');
    triggerTextDownload(`ClearEqual_Report_${report?.id || 'export'}_${dateStamp()}.txt`, lines);
  };

  const handlePreviewReport = (report) => {
    setSelectedReport(report);
    setIsPreviewOpen(true);
  };

  const handleDeleteReport = (reportId) => {
    setReports(prev => prev?.filter(r => r?.id !== reportId));
  };

  const handleExportJob = (job) => {
    const lines = [
      '='.repeat(72),
      'CLEAREQUAL, JOB DESCRIPTION EXPORT',
      '='.repeat(72),
      '',
      `Job ID:       ${job?.id || ''}`,
      `Title:        ${job?.title || job?.name || ''}`,
      `Department:   ${job?.department || ''}`,
      `Job level:    ${job?.level || ''}`,
      '',
      `Export generated: ${dateStamp()}`,
    ].join('\n');
    triggerTextDownload(`ClearEqual_Job_${job?.id || 'export'}_${dateStamp()}.txt`, lines);
  };

  const handlePreviewJob = (job) => {
    console.log('Previewing job description:', job?.id);
  };

  const handleDeleteJob = (jobId) => {
    setJobDescriptions(prev => prev?.filter(j => j?.id !== jobId));
  };

  const handleExportAuditLog = (logs) => {
    if (!logs || logs.length === 0) {
      triggerTextDownload(`ClearEqual_AuditLog_${dateStamp()}.csv`, 'No audit log entries\n');
      return;
    }
    triggerCsvDownload(`ClearEqual_AuditLog_${dateStamp()}.csv`, logs);
  };

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/analytics-dashboard' },
    { label: 'Compliance Reports', path: '/compliance-reports' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <Breadcrumb items={breadcrumbItems} />
          
          {/* Page Header */}
          <div className="flex items-center justify-between mb-2 gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  {t('reportsPage.title')}
                </span>
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-muted text-muted-foreground uppercase tracking-wide">
                  Art. 9
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
                {tenant.name}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {t('reportsPage.subtitle')}
              </p>
            </div>

            <div className="flex items-center space-x-3">
              <div className="flex items-center bg-muted rounded-lg p-1">
                <button
                  onClick={() => setActiveView('reports')}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-smooth ${
                    activeView === 'reports' ?'bg-card text-foreground shadow-soft' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Reports
                </button>
                <button
                  onClick={() => setActiveView('jobs')}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-smooth ${
                    activeView === 'jobs' ?'bg-card text-foreground shadow-soft' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Job Descriptions
                </button>
                <button
                  onClick={() => setActiveView('audit')}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-smooth ${
                    activeView === 'audit' ?'bg-card text-foreground shadow-soft' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Audit Trail
                </button>
                <button
                  onClick={() => setActiveView('aiAct')}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-smooth ${
                    activeView === 'aiAct' ?'bg-card text-foreground shadow-soft' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  EU AI Act
                </button>
              </div>
              
              <Button variant="outline" iconName="HelpCircle" iconPosition="left">
                Help Guide
              </Button>
            </div>
          </div>

          {/* Tenant context strip */}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 pb-4 mb-6 border-b border-border text-xs">
            <div className="flex items-center gap-1.5">
              <Icon name="Building2" size={12} className="text-muted-foreground" />
              <span className="text-muted-foreground">{t('payBands.metaIndustry')}</span>
              <span className="font-medium text-foreground">{tenant.industry}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Icon name="MapPin" size={12} className="text-muted-foreground" />
              <span className="text-muted-foreground">{t('reportsPage.jurisdiction')}</span>
              <span className="font-medium text-foreground">{tenant.country}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Icon name="FileText" size={12} className="text-muted-foreground" />
              <span className="text-muted-foreground">{t('reportsPage.reports')}</span>
              <span className="font-medium text-foreground tabular-nums">{reports?.length || 0}</span>
            </div>
            <div className="flex items-center gap-1.5 ml-auto">
              <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
              <span className="text-muted-foreground">{t('reportsPage.auditLog')}</span>
              <span className="font-medium text-foreground">{t('reportsPage.immutable')}</span>
            </div>
          </div>

          <div className={`transition-opacity duration-300 ${isTransitioning ? 'opacity-40' : 'opacity-100'}`}>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('complianceReportsPage.stats.totalReports')}</p>
                  <p className="text-2xl font-bold text-foreground">{reports?.length}</p>
                </div>
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="FileText" size={20} className="text-primary" />
                </div>
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('complianceReportsPage.stats.jobDescriptions')}</p>
                  <p className="text-2xl font-bold text-foreground">{jobDescriptions?.length}</p>
                </div>
                <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                  <Icon name="Brain" size={20} className="text-accent" />
                </div>
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('complianceReportsPage.stats.processing')}</p>
                  <p className="text-2xl font-bold text-foreground">
                    {reports?.filter(r => r?.status === 'processing')?.length}
                  </p>
                </div>
                <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
                  <Icon name="Clock" size={20} className="text-warning" />
                </div>
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('complianceReportsPage.stats.auditEvents')}</p>
                  <p className="text-2xl font-bold text-foreground">{auditLogs?.length}</p>
                </div>
                <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                  <Icon name="Shield" size={20} className="text-accent" />
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="space-y-8">
            {activeView === 'reports' && (
              <>
                {/* Report Configuration */}
                <ReportConfigurationPanel 
                  onGenerateReport={handleGenerateReport}
                  isGenerating={isGenerating}
                />
                
                <ReportsLibrary
                  reports={reports}
                  onExportReport={handleExportReport}
                  onPreviewReport={handlePreviewReport}
                  onDeleteReport={handleDeleteReport}
                />
              </>
            )}

            {activeView === 'jobs' && (
              <>
                {/* Skill Ontology Panel */}
                <SkillOntologyPanel 
                  onUpdateJobDescriptions={handleUpdateJobDescriptions}
                />
                
                <JobDescriptionLibrary
                  jobDescriptions={jobDescriptions}
                  onExportJob={handleExportJob}
                  onPreviewJob={handlePreviewJob}
                  onDeleteJob={handleDeleteJob}
                />
              </>
            )}

            {activeView === 'audit' && (
              <AuditTrailPanel
                auditLogs={auditLogs}
                onExportAuditLog={handleExportAuditLog}
              />
            )}

            {activeView === 'aiAct' && (
              <AIActCompliancePanel />
            )}
          </div>
          </div>
        </div>
      </main>
      {/* Report Preview Modal */}
      <ReportPreviewModal
        report={selectedReport}
        isOpen={isPreviewOpen}
        onClose={() => {
          setIsPreviewOpen(false);
          setSelectedReport(null);
        }}
        onExport={handleExportReport}
        onValidate={(report) => console.log('Validating report:', report?.id)}
      />
    </div>
  );
};

export default ComplianceReports;